import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { useStore } from '@/store';
import { useAuth } from '@/services/auth';
import Create from '@/views/Create.vue';
import Settings from '@/views/Settings.vue';
import Bids from '@/views/managers/BidsManager.vue';
import Bid from '@/views/CreateOrEditBid.vue';
import Map from '@/views/Map.vue';
import SignIn from '@/views/SignIn.vue';
import JoinGroupWithLink from '@/views/JoinGroupWithLink.vue';
import VendorsManager from '@/views/managers/VendorsManager.vue';
import MaterialsManager from '@/views/managers/MaterialsManager.vue';
import UsersManager from '@/views/managers/UsersManager.vue';
import GroupCreateOrJoin from '@/views/GroupCreateOrJoin.vue';
import ClientsManager from '@/views/managers/ClientsManager.vue';
import AppDefaultsManager from '@/views/managers/AppDefaultsManager.vue';
import TrucksManager from '@/views/managers/TrucksManager.vue';
import UserSettingsManager from '@/views/managers/UserSettingsManager.vue';

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/sign-in',
    name: 'Sign In',
    component: SignIn
  },
  {
    path: '/group-create-or-join',
    name: 'Group Create Or Join',
    component: GroupCreateOrJoin,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/join-group-with-link/:linkId',
    name: 'Join Group With Link',
    component: JoinGroupWithLink,
    props: true
  },
  {
    path: '/',
    alias: '/bids',
    name: 'Bids',
    component: Bids,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/create',
    name: 'Create',
    component: Create,
    redirect: '/create/map',
    children: [
      {
        path: '/create/map',
        name: 'Map',
        component: Map,
        meta: {
          requiresAuth: true,
          icon: 'map'
        }
      },
      {
        path: '/create/bid',
        name: 'Bid',
        component: Bid,
        meta: {
          requiresAuth: true,
          icon: 'receipt'
        }
      }
    ]
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    redirect: '/settings/vendors',
    children: [
      {
        path: '/settings/clients',
        name: 'Customers',
        component: ClientsManager,
        meta: {
          requiresAuth: true,
          icon: 'business'
        }
      },

      {
        path: '/settings/vendors',
        name: 'Vendors',
        component: VendorsManager,
        meta: {
          requiresAuth: true,
          icon: 'store'
        }
      },
      {
        path: '/settings/materials',
        name: 'Materials',
        component: MaterialsManager,
        meta: {
          requiresAuth: true,
          icon: 'category'
        }
      },
      {
        path: '/settings/users',
        name: 'Users',
        component: UsersManager,
        meta: {
          requiresAuth: true,
          icon: 'people'
        }
      },
      {
        path: '/settings/trucks',
        name: 'Trucks',
        component: TrucksManager,
        meta: {
          requiresAuth: true,
          icon: 'local_shipping'
        }
      },
      {
        path: '/settings/defaults',
        name: 'Defaults',
        component: AppDefaultsManager,
        meta: {
          requiresAuth: true,
          icon: 'build'
        }
      },
      {
        path: '/settings/user-settings',
        name: 'User Settings',
        component: UserSettingsManager,
        meta: {
          requiresAuth: true,
          icon: 'manage_accounts'
        }
      }
    ]
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes
});

export default router;

router.beforeEach(async to => {
  if (to.path === '/sign-in') return true;

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  if (!requiresAuth) return true;

  const { ensureAuth } = useAuth();
  const store = useStore();

  const vuexData = localStorage.getItem('vuex');
  let expirationTime;
  if (vuexData) {
    const vuexObject = JSON.parse(vuexData);

    if (vuexObject.auth.firebaseUser != null && vuexObject.auth.firebaseUser.stsTokenManager != null) {
      expirationTime = vuexObject.auth.firebaseUser.stsTokenManager.expirationTime;
    } else {
      expirationTime = null;
    }
  }

  const now = Date.now();
  if (expirationTime && now >= expirationTime) {
    await store.dispatch.auth.signOut();
    return '/sign-in';
  }

  const isAuthenticated = store.getters.auth.isSignedIn && ensureAuth(store.dispatch.auth.signOut);
  if (!isAuthenticated) return '/sign-in';

  if (to.path === '/group-create-or-join') return true;

  const isInGroup = !!(await store.dispatch.groups.getGroupId());
  if (!isInGroup) return '/group-create-or-join';

  return true;
});
